import { engagementTargetNoun } from './network-engagement-target';
import { getActorDisplayName, type Notification } from './notifications';

/**
 * ONE POPUP FOR A NEW MEMBER'S BURST OF ATTENTION (#4096).
 *
 * In a member's first 5 days every upvote and emoji reaction on their work pops up the
 * moment it lands. Three people noticing a first post inside a minute is one moment, not
 * three popups: everything inside a 60 s window folds into the popup already on screen
 * ("Maya and 2 others noticed your work"), which sonner updates in place under one id.
 *
 * Pure and framework-free so the decision is proven in `shared/test/engagement-toast.spec.ts`;
 * the two listeners (town-square and the client SPA) keep only the rendering.
 */

/** The one sonner id every instant-engagement popup shares, so a new event replaces it in place. */
export const ENGAGEMENT_TOAST_ID = 'new-member-engagement';

/** Events this close to the first one fold into its popup. */
export const ENGAGEMENT_TOAST_WINDOW_MS = 60_000;

/** How long the popup stays up after its window closes. */
export const ENGAGEMENT_TOAST_LINGER_MS = 5_000;

/** The `notification_type` analytics records for these popups: one name for both kinds. */
export const ENGAGEMENT_TOAST_TYPE = 'new_member_engagement';

export type EngagementEvent = Pick<Notification, 'type' | 'targetType' | 'metadata' | 'actor'>;

type EngagementKind = 'vote' | 'reaction';

export interface EngagementActor {
  id: string;
  name: string;
  avatarUrl: string | null;
}

export interface EngagementToastState {
  windowStartedAt: number;
  /** `${kind}:${actorId}:${targetPath}` per counted event, so a replay never counts twice. */
  seen: Set<string>;
  /** Newest first, one entry per person. */
  actors: EngagementActor[];
  voteCount: number;
  reactionCount: number;
  emojis: string[];
  /** The one target every event so far was on; null once they span more than one. */
  targetHref: string | null;
  /** What the first event was on: post, comment or artwork. */
  noun: 'post' | 'comment' | 'artwork' | null;
  /** The first event's content preview, shown while the popup is about one event. */
  snippet: string | null;
  dismissed: boolean;
}

function readString(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  return trimmed === '' ? null : trimmed;
}

function kindOf(type: string): EngagementKind | null {
  const upper = type.toUpperCase();
  if (upper === 'COMMUNITY_VOTE') return 'vote';
  if (upper === 'COMMUNITY_REACTION') return 'reaction';
  return null;
}

/**
 * Should this event go to the coalesced popup? Every COMMUNITY_VOTE row is a new
 * member's (only the instant path writes one); a reaction says so on the row, because
 * reactions are written for everyone.
 */
export function isInstantEngagement(event: Pick<Notification, 'type' | 'metadata'>): boolean {
  const kind = kindOf(event.type);
  if (kind === 'vote') return true;
  return kind === 'reaction' && event.metadata?.instantToast === true;
}

/**
 * The hydrated person for one id. Hydration drops ids with no profile, so
 * `metadata.actors` is NOT index-aligned with `actorIds`: match on `userId`.
 */
function hydratedActor(metadata: Record<string, any>, id: string): Record<string, unknown> | null {
  if (!Array.isArray(metadata.actors)) return null;
  return metadata.actors.find((person: any) => String(person?.userId) === id) ?? null;
}

/**
 * Who this event is from. A reaction row names one person (`actorId` + the hydrated
 * actor); a vote row names everyone in `actorIds`, newest first, hydrated into
 * `metadata.actors`.
 */
function actorOf(kind: EngagementKind, event: EngagementEvent): EngagementActor | null {
  const metadata = event.metadata ?? {};
  if (kind === 'reaction') {
    const id = readString(metadata.actorId);
    if (!id) return null;
    return { id, name: getActorDisplayName(event), avatarUrl: event.actor?.profile?.avatarUrl ?? null };
  }
  const ids = Array.isArray(metadata.actorIds) ? metadata.actorIds : [];
  const id = ids.length > 0 ? readString(String(ids[0])) : null;
  if (!id) return null;
  const person = hydratedActor(metadata, id);
  const name = readString(person?.name) ?? 'Someone';
  return { id, name, avatarUrl: readString(person?.avatarUrl) };
}

function emojisOf(event: EngagementEvent): string[] {
  const held = event.metadata?.emojis;
  if (Array.isArray(held) && held.length > 0) return held.filter((emoji) => typeof emoji === 'string');
  const single = readString(event.metadata?.emoji);
  return single ? [single] : [];
}

function countKind(seen: Set<string>, kind: EngagementKind): number {
  return [...seen].reduce((memo, key) => (key.startsWith(`${kind}:`) ? memo + 1 : memo), 0);
}

function emptyWindow(nowMs: number, event: EngagementEvent, targetPath: string | null): EngagementToastState {
  const metadata = event.metadata ?? {};
  return {
    windowStartedAt: nowMs,
    seen: new Set(),
    actors: [],
    voteCount: 0,
    reactionCount: 0,
    emojis: [],
    targetHref: targetPath,
    noun: engagementTargetNoun(event.targetType ?? null),
    snippet: readString(metadata.contentSnippet) ?? readString(metadata.commentText),
    dismissed: false,
  };
}

function isWindowOpen(state: EngagementToastState | null, nowMs: number): state is EngagementToastState {
  if (!state) return false;
  return nowMs - state.windowStartedAt <= ENGAGEMENT_TOAST_WINDOW_MS;
}

/**
 * Fold one live event into the popup.
 *
 * Opens a new window when there is none or the last one is more than 60 s old; inside a
 * window it adds the event once. Returns the SAME state object when the event changes
 * nothing (a replay, a resurface, or a row naming nobody), so a caller can skip the
 * re-render. Events that are not instant engagement leave the state alone.
 */
export function coalesceEngagementToast(
  state: EngagementToastState | null,
  event: EngagementEvent,
  nowMs: number
): EngagementToastState | null {
  const kind = kindOf(event.type);
  if (!kind || !isInstantEngagement(event)) return state;
  const actor = actorOf(kind, event);
  if (!actor) return state;

  const targetPath = readString(event.metadata?.targetPath);
  const base = isWindowOpen(state, nowMs) ? state : emptyWindow(nowMs, event, targetPath);
  const key = `${kind}:${actor.id}:${targetPath ?? ''}`;
  if (base.seen.has(key)) return base;

  const seen = new Set(base.seen).add(key);
  return {
    ...base,
    seen,
    actors: [actor, ...base.actors.filter((existing) => existing.id !== actor.id)],
    voteCount: countKind(seen, 'vote'),
    reactionCount: countKind(seen, 'reaction'),
    emojis: kind === 'reaction' ? [...new Set([...base.emojis, ...emojisOf(event)])] : base.emojis,
    targetHref: base.targetHref === targetPath ? base.targetHref : null,
  };
}

/** The member closed it: the bell keeps every event, the popup stays away until the window closes. */
export function dismissEngagementToast(state: EngagementToastState | null): EngagementToastState | null {
  if (!state) return state;
  return { ...state, dismissed: true };
}

/** Until the window closes, plus a few seconds to read the last update. */
export function engagementToastDuration(state: EngagementToastState, nowMs: number): number {
  const remaining = Math.max(0, state.windowStartedAt + ENGAGEMENT_TOAST_WINDOW_MS - nowMs);
  return remaining + ENGAGEMENT_TOAST_LINGER_MS;
}

function plural(count: number, noun: string): string {
  return `${count} ${noun}${count === 1 ? '' : 's'}`;
}

/**
 * The popup's sentence, split so the card can bold the name: `{actor} {action}`.
 *
 * One event names what happened ("upvoted your post", "reacted ❤️ to your comment"). Any
 * more, from one person or several, reads as attention on the work as a whole.
 */
export function engagementToastHeadline(state: EngagementToastState): { actor: string; action: string } {
  const actor = state.actors[0]?.name ?? 'Someone';
  const noun = state.noun ?? 'work';
  if (state.seen.size === 1 && state.voteCount === 1) return { actor, action: `upvoted your ${noun}` };
  if (state.seen.size === 1) {
    const shown = state.emojis.join('') || '❤️';
    return { actor, action: `reacted ${shown} to your ${noun}` };
  }
  const others = state.actors.length - 1;
  if (others <= 0) return { actor, action: 'noticed your work' };
  return { actor, action: `and ${plural(others, 'other')} noticed your work` };
}

/** The second line: the content for one event, the tally ("2 upvotes · 1 reaction") for more. */
export function engagementToastSnippet(state: EngagementToastState): string | null {
  if (state.seen.size === 1) return state.snippet;
  const parts = [
    state.voteCount > 0 ? plural(state.voteCount, 'upvote') : null,
    state.reactionCount > 0 ? plural(state.reactionCount, 'reaction') : null,
  ].filter(Boolean);
  return parts.join(' · ');
}
